/**
 * Centralized Supabase services
 * 
 * All Supabase operations should go through these services
 * for consistency, reusability, and maintainability
 */

export { authService } from './auth'
export { getEnrollmentProgress } from './icbt-progress'

// NOTE: `./messaging` is intentionally NOT re-exported here.
// It uses `import 'server-only'` (needs `crypto.randomUUID`), so
// dragging it into this barrel breaks any client-component chain
// that touches `@repo/supabase` (the barrel is imported by
// logger.service etc.). Same reason `./sessions` sits on its own
// subpath — import from `@repo/supabase/messaging` directly.
export {
  resolveClientSourceApp,
  resolveNotificationEmails,
  type ClientEmailSourceApp,
} from './email-routing'
export { portalAuthService } from './portal-auth'
export type { PortalType, PortalAuthResult } from './portal-auth'
export { profileService, transformToPublicTherapist } from './profiles'
export { userService } from './user'
export { healthRecordService } from './health-record'
export { episodeService } from './episode'
export { storageService } from './storage'
export { auditService } from './audit'
export { onboardingService } from './onboarding'
export { createAvailabilityService } from './availability'
export { meetingService, createMeetingService } from './meetings'
export {
  createEventsService,
  canHostEvents,
  canHostEvent,
  canEditEvent,
  canSeeEvent,
} from './events'
export type {
  EventType,
  EventAudienceScope,
  EventEnrollmentMode,
  EventStatus,
  EventAttendeeStatus,
  EventAttendeeSource,
  PdActivityCategory,
  EventAudienceFilter,
  EventRow,
  EventAttendeeRow,
  EventSeriesRow,
  EventSeriesPattern,
  EventSeriesMonthlyMode,
  EventSeriesStatus,
  EventRecurrenceSpec,
  AuthzPro,
  CreateEventInput,
  UpdateEventInput,
  EventsService,
} from './events'
export { createErrorTrackingService } from './error-tracking'
export { errorLogger, getErrorLogger } from './error-logger'
export { saveQuizData } from './quiz'
export { saveTempQuizData, completeTempQuiz } from './temp-quiz'
export { findMatchingPros, updateMatchingQuizWithPros, runMatching, updateMatchingQuizWithBesPros } from './matching'
export { getProAvailability, createInitialSession, getTherapistSessions, getSessionAttendees, updateSessionStatus, getSessionsByEpisode, getSessionsByGroup, validateBooking, createBooking } from './session'
export { 
  getIntakeFormsByUserId, 
  getLatestIntakeFormByUserId, 
  getIntakeFormsByHealthRecordId, 
  getIntakeFormsByEpisode,
  getIntakeFormById 
} from './intake'
export { consentService } from './consent'
export { therapyGroupService } from './therapy-group'
export { developmentIssuesService } from './development-issues'
export {
  supportService,
  checkAnonRateLimit,
  createSupportTicket,
  appendSupportMessage,
  getSupportTicketByToken,
  stripInternalMessages,
} from './support'
export type {
  SupportTicketApp,
  SupportTicketCategory,
  SupportTicketStatus,
  SupportTicketPriority,
  SupportMessageRole,
  SupportMessageVisibility,
  SupportAttachment,
  SupportMessage,
  CreateSupportTicketInput,
  CreatedSupportTicket,
} from './support'
export { promoCodesService } from './promo-codes'
export { pickProEmail, isAlliInternal, PRO_EMAIL_SELECT } from './pro-email'
export type { ProEmailFields } from './pro-email'
export { refundAttendeeCycle } from './supervision-refund'
export type { RefundAttendeeResult } from './supervision-refund'
export {
  validatePromoCode,
  calculateDiscountedRate,
  getPromoCode,
  getPromoDisplayText
} from './promo'
export {
  createEmployerWallet,
  createPromoWallet,
  addAuthorizedUser,
  removeAuthorizedUser,
  findEmployeeWallet,
  findBestAvailableWallet,
  reserveWalletSession,
  updateWalletTransactionStatus,
  calculateWalletBalance,
  getTotalAvailableSessions,
  invalidateWallet,
  mapWalletSourceToPaymentSource
} from './wallet'
export { createAdminEmailService } from './admin-email'
export { 
  createProgressNote, 
  updateProgressNote, 
  getProgressNote, 
  getProgressNoteBySessionId,
  getProgressNotesByEpisode,
  getProgressNotesByGroup,
  deleteProgressNote 
} from './progress-notes'
export {
  createAssessment,
  updateAssessment,
  getAssessment,
  getAssessmentBySessionId,
  getAssessmentsByEpisode,
  getAssessmentsByGroup,
  deleteAssessment
} from './assessment'
export type { TherapistOnboardingData, OnboardingResult } from './onboarding'
export type { AvailabilityData, CreateAvailabilityData } from './availability'
export type { CreateMeetingData } from './meetings'
export type { ErrorLogData } from './error-tracking'
export type { LogErrorOptions } from './error-logger'
export type { QuizData } from './quiz'
export type { MatchingCriteria } from './matching'
export type { TimeSlot, AvailabilityResponse, CreateInitialSessionParams, Session, BookingValidation, CreateBookingParams, BookingResult } from './session'
export type { IntakeFormData } from './intake'
export type { UserProfileData } from './user'
export type { HealthRecordData } from './health-record'
export type { EpisodeData, CreateEpisodeParams } from './episode'
export type { ConsentData, CreateConsentInput } from './consent'
export type { ProgressNote, ProgressNoteData } from './progress-notes'
export type { Assessment, AssessmentData } from './assessment'
export type { PromoCode } from './promo'
export type { TherapyGroupData, TherapyGroupWithEpisodes } from './therapy-group'
export type { DevelopmentIssue, CreateIssueInput, UpdateIssueInput } from './development-issues'
export type { PromoCode as PromoCodeData, CreatePromoCodeInput, UpdatePromoCodeInput } from './promo-codes'
export type { SessionWallet, CreateEmployerWalletParams, WalletBalance, WalletTransactionStatus } from './wallet'
export type { ChangeEmailParams, ChangeEmailResult } from './admin-email'
export { renewEmployerProgram, runProgramRenewalJob } from './program-renewal'
export {
  careService,
  createCareRequest,
  supervisorApprove as supervisorApproveCare,
  supervisorReject as supervisorRejectCare,
  cancelActive as cancelActiveCare,
  expireRequest as expireCareRequest,
  listForTherapist as listCareForTherapist,
  listForSupervisor as listCareForSupervisor,
  countActiveForTherapist as countActiveCareForTherapist,
  countPendingForSupervisor as countPendingCareForSupervisor,
  getCareRequest,
  signedProofUrl as signedCareProofUrl,
  listRequestsToExpire as listCareRequestsToExpire,
  listRequestsForReminder as listCareRequestsForReminder,
  CareError,
  CARE_RATE_BANDS,
  CARE_MAX_DURATION_MONTHS,
  CARE_MAX_ACTIVE_PER_THERAPIST,
  CARE_HARDSHIP_PROOF_BUCKET,
} from './care'
export type {
  CareTier,
  CareRequestStatus,
  CareProofDocument,
  CareRequest,
  CreateCareRequestInput,
} from './care'

// NOTE: `./sessions` is intentionally not re-exported here.
// `cancelUserSession` imports `@repo/email`, which pulls in `@sendgrid/mail`
// (needs Node `fs`). Including it in this barrel drags SendGrid into every
// client bundle that touches `@repo/supabase`. Import from the dedicated
// subpath instead: `import { ... } from '@repo/supabase/sessions'`.

// Re-export the supabase client for direct access when needed
export { supabase } from '../client/browser'